<script lang="ts">
  import * as _ from "lodash";
  import { onMount } from "svelte";
  import MainView from "./MainView.svelte";
  import Filters from "@shared/svelte/Components/ProductLines/Filters.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import { allFilterTypes } from "@shared/services/filters";
  import getFilterParamsFromState from "@retailer/services/filter_params_from_state_params.js";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";

  import type { FiltersType, ProductChooserOptionsType } from "@shared/types/Filters";
  import type { ProductLine } from "@shared/types/ProductLine";

  export let onCloseProductChooser: any;
  export let pullFilters: any;
  export let productLines: ProductLine[];
  export let pullProductLines: any;
  export let onSelectProduct: any;
  export let productChooserOptions: ProductChooserOptionsType = {};
  export let initialFilterStateParams: any = {};
  export let externalLoading: boolean = false;

  export let selectMultipleChoices: object = {};

  let maxProductsSelected: boolean = false;
  let filters: FiltersType;
  let numSelectedFilters: number = 0;
  let productLineParams = initialFilterStateParams || {};
  let updateFiltersOnLoad = initialFilterStateParams !== null;
  let disableFilters: boolean = false;
  let productLinesLoading: boolean = false;
  let filtersLoading: boolean = false;
  let disabledFilterRolloutIds: any = [];

  $: showFiltersInMobile = false;
  $: mobileMode = $LayoutStore.isMobile;

  const cleanParams = (params: any) => {
    if (disabledFilterRolloutIds.length) {
      disabledFilterRolloutIds.forEach(id => {
        const filter = allFilterTypes.find(f => f.id === id);
        delete params[filter.param];
        delete params[filter.key];
      });
    }
    return params;
  };

  function runPullFilters(newProductLineParams: any) {
    const params = cleanParams(newProductLineParams || {});
    pullFilters(params).then(
      function success(res: any) {
        filters = res.data;
        filtersLoading = false;
      },
      function error(res: any) {
        console.error(res);
        filtersLoading = false;
      }
    );
    productLineParams = params;
  }

  function searchProductLines(value: any) {
    if (!productLineParams) {
      productLineParams = {};
    }
    productLineParams.search = value?.length ? value : "";
    onChange(productLineParams);
  }

  function onChange(newProductLineParams: any) {
    productLinesLoading = true;
    filtersLoading = true;

    // This could be called in Filters, which doesn't know about any search term
    newProductLineParams = cleanParams(newProductLineParams);
    newProductLineParams.search = productLineParams?.search;

    pullProductLines(newProductLineParams).then(
      function success(res: any) {
        productLines = res.data;
        productLinesLoading = false;
      },
      function error(res: any) {
        productLinesLoading = false;
        console.error(res);
      }
    );
    runPullFilters(newProductLineParams);
  }

  onMount(function () {
    onChange(cleanParams(initialFilterStateParams));
  });

  function getSelectedLength() {
    return _.keys(_.pickBy(selectMultipleChoices)).map(function (x: any) {
      return parseInt(x);
    }).length;
  }

  function handleFilterCountChange(event: any) {
    numSelectedFilters = event.detail.filterCount;
  }

  let selectedLength = getSelectedLength();
  $: {
    selectMultipleChoices = selectMultipleChoices;
    selectedLength = getSelectedLength();
    maxProductsSelected = selectedLength >= productChooserOptions.selectMax;
    disableFilters = productLinesLoading || filtersLoading;
  }

  let filtersRerenderKey: number = 0;
  $: {
    filtersRerenderKey = filters?.selected_filters?.length || 0;
  }

  const searchFilters = changes => {
    const newParams = {
      ...getFilterParamsFromState(changes),
      ...changes,
    };

    runPullFilters(newParams);
  };

  const excludedFilterKeys = [
    "frontline_price",
    "future_pricing",
    "deals",
    "in_stock",
    "my_distributors",
    "previously_ordered",
  ];

</script>

<div class="fizz-layout-fixed-header-and-sidebar ">
  <header class="fizz-space-between" data-grid-area="header">
    {#if !productChooserOptions.selectMultiple}
      <h4 class="fizz-heading-4">Select a product to feature</h4>
    {:else}
      <h4 class="fizz-heading-4">Select multiple products</h4>
    {/if}
    <!-- svelte-ignore a11y-missing-attribute -->
    <a
      on:click={onCloseProductChooser}
      class="fizz-modal-toggle cursor-pointer"
      data-modal="featuredProduct"
      id="close-product-chooser"
      data-testid="close-product-chooser"
    >
      <Icon type="X" extraClassNames="fizz-text-brand" height="20" width="20" />
    </a>
  </header>
  {#if mobileMode ? showFiltersInMobile : true}
    {#key filtersRerenderKey}
      {#if filters}
        <div data-testid="side-filter-wrapper" class="fizz-layout-sidebar-main fizz-overflow-y">
          <Filters
            bind:filters
            {onChange}
            onSearch={searchFilters}
            on:filterCountChange={handleFilterCountChange}
            stateParams={initialFilterStateParams}
            updateOnLoad={updateFiltersOnLoad}
            disableFiltersDuringProcessing={true}
            excludedKeys={excludedFilterKeys}
            bind:disableFilters
            bind:showFiltersInMobile
            bind:disabledFilterRolloutIds
            bind:mobileMode
          />
        </div>
      {/if}
    {/key}
  {/if}
  <section data-grid-area="main" class={showFiltersInMobile ? "fizz-hide-on-small" : ""}>
    <MainView
      {productLines}
      {searchProductLines}
      bind:selectMultipleChoices
      {numSelectedFilters}
      {onSelectProduct}
      {productChooserOptions}
      {maxProductsSelected}
      bind:loading={productLinesLoading}
      bind:showFiltersInMobile
    />
  </section>

  {#if productChooserOptions.selectMultiple}
    <footer data-grid-area="footer" class="fizz-flex-row-24 fizz-justify-end" id="pcmodal-footer">
      {#if productChooserOptions.selectMultiple}
        <strong>
          Selected {productChooserOptions.maxRequired
            ? "(" + selectedLength + "/" + productChooserOptions.selectMax + ")"
            : selectedLength}
        </strong>
      {/if}
      <button
        class="fizz-button-primary fizz-modal-toggle"
        data-modal="productCards"
        data-testid="product-chooser-add-products"
        disabled={externalLoading}
        on:click={onSelectProduct(selectMultipleChoices)}>Add Products</button
      >
    </footer>
  {/if}
</div>
